import type {
  BookPerformanceResponse,
  BrandId,
  CommissionLeaderboardResponse,
  CommissionStatementDetailResponse,
  CommissionStatementsListResponse,
  SkuSpreadResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/commission-reports/*`
 * (epic #1034 E5-M5, #1147). ops-api derives scope from the session role;
 * the console never sends a scope param. `brand` is optional: a lead omits
 * it (ops-api pins their profile brand), an admin passes it explicitly.
 *
 * ATTRIBUTION LAYERING: commission statements read receipt attribution;
 * book performance reads lot attribution — the two are never blended, and
 * the pages label them apart.
 */

const BASE_PATH = '/v1/ops/commission-reports';

export const COMMISSION_REPORTS_QUERY_KEY = ['commission-reports'] as const;

function buildQuery(params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value.length > 0) {
      search.set(key, value);
    }
  }
  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}

// ─── commission statements (RECEIPT attribution) ────────────────────────────

export function useCommissionStatements(
  params: { brand: BrandId | null; period?: string; status?: 'draft' | 'confirmed' },
  enabled = true,
): UseQueryResult<CommissionStatementsListResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      ...COMMISSION_REPORTS_QUERY_KEY,
      'statements',
      params.brand ?? 'scoped',
      params.period ?? 'all',
      params.status ?? 'any',
    ],
    queryFn: ({ signal }) =>
      opsApiRequest<CommissionStatementsListResponse>(
        `${BASE_PATH}/statements${buildQuery({
          brand: params.brand,
          period: params.period,
          status: params.status,
        })}`,
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useCommissionStatementDetail(
  id: string | null,
  brand: BrandId | null,
  enabled = true,
): UseQueryResult<CommissionStatementDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...COMMISSION_REPORTS_QUERY_KEY, 'statement', id ?? 'none', brand ?? 'scoped'],
    queryFn: ({ signal }) =>
      opsApiRequest<CommissionStatementDetailResponse>(
        `${BASE_PATH}/statements/${encodeURIComponent(id ?? '')}${buildQuery({ brand })}`,
        { signal },
        fetchImpl,
      ),
    enabled: enabled && id !== null,
  });
}

// ─── book performance (LOT attribution) ─────────────────────────────────────

export function useBookPerformance(
  params: { brand: BrandId | null; from: string; to: string },
  enabled = true,
): UseQueryResult<BookPerformanceResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      ...COMMISSION_REPORTS_QUERY_KEY,
      'book',
      params.brand ?? 'scoped',
      params.from,
      params.to,
    ],
    queryFn: ({ signal }) =>
      opsApiRequest<BookPerformanceResponse>(
        `${BASE_PATH}/book-performance${buildQuery({
          brand: params.brand,
          from: params.from,
          to: params.to,
        })}`,
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

// ─── leaderboard (commission + book side by side) ───────────────────────────

export function useCommissionLeaderboard(
  params: { brand: BrandId | null; period: string },
  enabled = true,
): UseQueryResult<CommissionLeaderboardResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...COMMISSION_REPORTS_QUERY_KEY, 'leaderboard', params.brand ?? 'scoped', params.period],
    queryFn: ({ signal }) =>
      opsApiRequest<CommissionLeaderboardResponse>(
        `${BASE_PATH}/leaderboard${buildQuery({ brand: params.brand, period: params.period })}`,
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

// ─── spread report (procurement vs sales cost — lead+admin) ─────────────────

export function useSkuSpread(
  brand: BrandId | null,
  enabled = true,
): UseQueryResult<SkuSpreadResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...COMMISSION_REPORTS_QUERY_KEY, 'spread', brand ?? 'scoped'],
    queryFn: ({ signal }) =>
      opsApiRequest<SkuSpreadResponse>(
        `${BASE_PATH}/spread${buildQuery({ brand })}`,
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}
